import React, { useState, useRef, useEffect } from 'react';

const initials = (employee) => {
  if (!employee) return '?';
  const name = employee.display_name || employee.username || '';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

/**
 * Popover checklist for assigning a ticket to more than one person at
 * once (Jira-classic only allows a single assignee; this platform's
 * tickets can go to several people at the same time, e.g. two techs
 * working an order together).
 *
 * Checkbox toggles are staged locally (`pendingIds`) and only sent to the
 * parent's `onChange` once, when the popover closes (click-away, Escape,
 * or the "Done" button) -- NOT on every individual click. This matters
 * for RBAC: a non-admin unchecking themselves mid-reassignment would
 * otherwise fire an immediate save after that single click, instantly
 * losing visibility of the ticket before they get a chance to check the
 * new assignee in the same interaction. Batching means "uncheck me, check
 * someone else" always lands as one atomic update.
 *
 * Props:
 *   - employees: full employee directory [{ mavis_user_id, display_name, username }]
 *   - selectedIds: array of mavis_user_id currently assigned
 *   - onChange: (newSelectedIds) => void
 *   - disabled, id
 */
const AssigneeMultiSelect = ({ employees, selectedIds, onChange, disabled, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState(selectedIds);
  const containerRef = useRef(null);
  const pendingRef = useRef(pendingIds);
  const selectedRef = useRef(selectedIds);
  const onChangeRef = useRef(onChange);
  pendingRef.current = pendingIds;
  selectedRef.current = selectedIds;
  onChangeRef.current = onChange;

  const commitIfChanged = () => {
    const pending = pendingRef.current;
    const current = selectedRef.current;
    const changed = pending.length !== current.length ||
      pending.some((id_) => !current.includes(id_)) ||
      current.some((id_) => !pending.includes(id_));
    if (changed) onChangeRef.current(pending);
  };

  const openPopover = () => {
    setPendingIds(selectedIds);
    setIsOpen(true);
  };

  const closePopover = () => {
    setIsOpen(false);
    commitIfChanged();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closePopover();
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayIds = isOpen ? pendingIds : selectedIds;
  const selected = employees.filter((e) => displayIds.includes(e.mavis_user_id));

  const toggle = (empId) => {
    setPendingIds((prev) =>
      prev.includes(empId) ? prev.filter((id_) => id_ !== empId) : [...prev, empId]
    );
  };

  return (
    <div className="date-picker" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="date-picker-trigger form-input"
        onClick={() => !disabled && (isOpen ? closePopover() : openPopover())}
        disabled={disabled}
      >
        {selected.length === 0 ? (
          <span>Unassigned</span>
        ) : (
          <span style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {selected.map((emp) => (
              <span key={emp.mavis_user_id} className="avatar" style={{ width: 20, height: 20, fontSize: '0.6rem' }} title={emp.display_name}>
                {initials(emp)}
              </span>
            ))}
          </span>
        )}
        <span aria-hidden="true">&#9662;</span>
      </button>

      {isOpen && (
        <div className="date-picker-popover" role="listbox" aria-label="Choose assignees" style={{ width: 260, maxHeight: 260, overflowY: 'auto' }}>
          {employees.length === 0 && <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>No employees found.</p>}
          {employees.map((emp) => (
            <label
              key={emp.mavis_user_id}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.2rem', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={pendingIds.includes(emp.mavis_user_id)}
                onChange={() => toggle(emp.mavis_user_id)}
              />
              <span className="avatar" style={{ width: 22, height: 22, fontSize: '0.65rem' }}>{initials(emp)}</span>
              {emp.display_name || emp.username}
            </label>
          ))}
          <button
            type="button"
            className="btn-secondary"
            style={{ width: '100%', marginTop: '0.4rem' }}
            onClick={closePopover}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default AssigneeMultiSelect;
